<template>
  <div class="page-default">
    <div class="dashboard-section bg-dashboard-dark-2">
      <div class="cases-group">
        <div class="row h-100">
          <div
            class="col-12 h-auto d-flex justify-content-end align-items-center"
          >
            <div class="dropdown mr-3">
              <button
                class="btn btn-sm btn-dark dropdown-toggle text-white"
                type="button"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                <font-awesome-icon :icon="['fas', 'calendar']" class="mr-2" />
                {{
                  dateFilter && dateFilter.start && dateFilter.end
                    ? `${dateFilter.start} → ${dateFilter.end}`
                    : "Date range"
                }}
              </button>
              <div
                class="dropdown-menu p-2 bg-dark border-outline-dark"
                style="min-width: 280px"
              >
                <div
                  class="d-flex justify-content-between align-items-center mb-2"
                >
                  <small class="text-light">Filter by date</small>
                  <button
                    class="btn btn-sm btn-link text-white p-0"
                    @click="dateFilter = null"
                  >
                    Clear
                  </button>
                </div>
                <div @click.stop>
                  <v-date-picker
                    is-dark
                    class="inline-calendar"
                    v-model="dateFilter"
                    :model-config="{
                      type: 'string',
                      mask: 'YYYY-MM-DD',
                    }"
                    mode="date"
                    trim-weeks
                    is-range
                  />
                </div>
              </div>
            </div>
            <div
              class="d-flex justify-content-end align-items-center text-muted"
            >
              <label
                class="custom-control-label no-pseudo pr-2 small clickable"
                for="switchLocal"
                :class="{ 'text-primary font-weight-medium': !isLocal }"
                data-placement="top"
                @mouseover="helper.showTooltip"
                @mouseleave="helper.hideTooltip"
                title="Filter sources"
              >
                All
              </label>
              <div class="custom-control custom-switch">
                <input
                  type="checkbox"
                  class="custom-control-input"
                  id="switchLocal"
                  v-model="isLocal"
                />
                <label class="custom-control-label clickable" for="switchLocal">
                  &nbsp;
                </label>
              </div>
              <label
                class="custom-control-label no-pseudo small clickable"
                for="switchLocal"
                :class="{ 'text-primary font-weight-medium': isLocal }"
                data-toggle="tooltip"
                data-placement="top"
                @mouseover="helper.showTooltip"
                @mouseleave="helper.hideTooltip"
                title="Filter sources"
              >
                Selangor only
              </label>
            </div>
          </div>
          <div
            v-if="isLoaded && computedCases.length < 1"
            class="small text-muted font-italic h-100 w-100 d-flex justify-content-center align-items-center"
          >
            No social media cases found.
          </div>
          <div
            class="col-12 col-sm-6 col-md-4"
            v-for="(c, index) in computedCases"
            :key="`cases${index}`"
            style="height: calc(100% - 46px)"
          >
            <div class="case-container bg-dashboard-dark-1 d-flex flex-column">
              <div class="case-header text-uppercase text-white">
                <div class="case-title">
                  <font-awesome-icon
                    :icon="getCaseIcon(c.type).i"
                    class="mr-2"
                    :class="`text-${getCaseIcon(c.type).color}`"
                  />{{ helper.titleCase(c.type) }} ({{ c.total }} total)
                </div>
              </div>
              <div class="case-body flex-grow-1">
                <div
                  class="case-list no-pointer"
                  v-for="cl in c.pageData"
                  :key="cl._id"
                >
                  <div
                    :class="
                      c.type !== 'textMessage' &&
                      'd-flex justify-content-between'
                    "
                  >
                    <div
                      class="media"
                      :style="
                        c.type !== 'textMessage' &&
                        'width: 60%; work-break: break-word;'
                      "
                    >
                      <img
                        :src="cl.socmedAvatarUrl"
                        class="mr-3 media-image"
                        v-if="cl.socmedAvatarUrl"
                        @error="onImageError(cl, $event)"
                      />
                      <img
                        src="@/assets/pothole.jpg"
                        class="mr-2 media-image"
                        v-else-if="cl.type == 'waze'"
                      />
                      <img
                        src="@/assets/user.jpg"
                        class="mr-2 media-image"
                        v-else
                      />
                      <div class="media-body">
                        <p class="media-title mb-0 text-white">
                          {{ cl.socmedUsername }}
                        </p>
                        <div>
                          <p
                            class="text-muted small mb-2 d-inline-block"
                            :title="getMoment('title', cl.timestamp)"
                          >
                            {{ getMoment("display", cl.timestamp) }}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div v-if="c.type !== 'textMessage'">
                      <button
                        class="btn btn-primary rounded-pill"
                        @click="goToCase(cl, c.type)"
                        style="font-size: 10px"
                        role="button"
                        data-placement="top"
                        @mouseover="helper.showTooltip"
                        @mouseleave="helper.hideTooltip"
                        :title="c.type === 'twitter' ? 'View on ' + capitalizeFirstLetter(c.type) : 'View Case'"
                      >
                        <font-awesome-icon
                          :icon="getCaseIcon(c.type).i"
                          class="fa-sm mr-2"
                          :class="`text-light`"
                        />{{ c.type === 'twitter' ? 'View on ' + capitalizeFirstLetter(c.type) : 'View Case' }}
                      </button>
                    </div>
                  </div>
                  <p class="mt-3">
                    <text-highlighter
                      class="text-white"
                      :type="cl.type"
                      :text="cl.caption"
                    />
                  </p>
                  <div
                    class="p-1 pb-4 mt-3 w-100 clickable"
                    v-for="cm in cl.media"
                    :key="cm._id"
                    :id="`dashboardMedia${cm._id}`"
                    @click="currentImage = cm"
                  >
                    <div
                      class="py-3 w-100 d-flex justify-content-center align-items-center text-muted"
                    >
                      <span
                        class="spinner-border spinner-border-sm mr-2"
                        role="status"
                      >
                      </span>
                      Loading media...
                    </div>
                    {{ loadImage(cm) }}
                  </div>
                  <div
                    class="dropdown-divider mx-2 border-top"
                    style="border-color: #111111 !important"
                  ></div>
                </div>
              </div>
              <div
                v-if="c.totalPages > 1"
                class="case-footer d-flex justify-content-between align-items-center px-2 py-2 border-top border-outline-dark"
              >
                <small class="text-secondary">
                  Page {{ c.page }} of {{ c.totalPages }}
                </small>
                <nav :aria-label="`${c.type} pagination`">
                  <ul class="pagination pagination-sm m-0">
                    <li class="page-item">
                      <button
                        type="button"
                        class="btn btn-sm text-white rounded-0 bg-dark"
                        :disabled="c.page <= 1"
                        @click="goToPage(index, c.page - 1)"
                      >
                        Prev
                      </button>
                    </li>
                    <li
                      class="page-item"
                      v-for="(pg, pIdx) in c.paginationPages"
                      :key="`${c.type}-pg-${pIdx}`"
                    >
                      <button
                        v-if="pg !== null"
                        type="button"
                        class="btn btn-sm text-white rounded-0"
                        :class="pg === c.page ? 'bg-primary' : 'bg-dark'"
                        @click="goToPage(index, pg)"
                      >
                        {{ pg }}
                      </button>
                      <span
                        v-else
                        class="btn btn-sm text-white rounded-0 bg-dark disabled"
                      >
                        &hellip;
                      </span>
                    </li>
                    <li class="page-item">
                      <button
                        type="button"
                        class="btn btn-sm text-white rounded-0 bg-dark"
                        :disabled="c.page >= c.totalPages"
                        @click="goToPage(index, c.page + 1)"
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <media-preview
      v-if="currentImage"
      :url="helper.formatMediaUrl(currentImage)"
      @close="currentImage = null"
    />
    <spinner v-if="isProcessing" />
  </div>
</template>

<script>
import Moment from "moment";
import API from "@/api";
import Helper from "@/helper";
import Spinner from "@/components/Spinner";
import MediaPreview from "@/components/MediaPreview";
import TextHighlighter from "@/components/TextHighlighter";

export default {
  name: "social",
  components: {
    MediaPreview,
    spinner: Spinner,
    "text-highlighter": TextHighlighter,
  },
  data() {
    return {
      helper: Helper,
      currentImage: null,
      isProcessing: false,
      isLoaded: false,
      cases: [],
      isLocal: true,
      pageSize: 25,
      dateFilter: null
    };
  },
  watch: {
    dateFilter: {
      handler() {
        this.cases.forEach((c) => this.$set(c, "page", 1));
        this.getCases();
      },
      deep: true,
    },
  },
  computed: {
    computedCases() {
      return this.cases.map((c) => {
        const data = this.isLocal
          ? c.data.filter((d) => d.isLocal === true)
          : c.data;
        const total = data.length;
        const totalPages = Math.max(1, Math.ceil(total / this.pageSize));
        const page = Math.min(Math.max(c.page || 1, 1), totalPages);
        const startIdx = (page - 1) * this.pageSize;
        return {
          type: c.type,
          data,
          total,
          totalPages,
          page,
          pageData: data.slice(startIdx, startIdx + this.pageSize),
          paginationPages: this.computePaginationPages(page, totalPages),
        };
      });
    },
    computedListCases() {
      let _cases = JSON.parse(JSON.stringify(this.cases));

      _cases = _cases
        .map((_case) => _case.data)
        .flat()
        .filter((_case) => _case.lat && _case.lng);

      if (this.isLocal) {
        _cases = _cases.filter((_case) => _case.isLocal);
      }

      return _cases;
    },
  },
  methods: {
    capitalizeFirstLetter(str) {
      let string = str.charAt(0).toUpperCase() + str.slice(1);
      let result = string.replace(/([a-z])([A-Z])/g, "$1 $2");
      return result;
    },
    goToSocMedia(url) {
      window.open(url.socmedPostUrl, "_blank");
    },
    goToCase(cl, type) {
      if (type === "twitter") {
        this.goToSocMedia(cl);
      } else {
        window.open(`${window.location.origin}/#/complaints`, "_blank");
      }
    },
    loadImage(cm) {
      let img = cm.mime.includes("image")
        ? new Image()
        : document.createElement("VIDEO");

      img.className = "w-100 rounded shadow";

      if (cm.mime.includes("video")) {
        let source = document.createElement("source");

        img.onloadeddata = () => {
          let parent = document.getElementById(`dashboardMedia${cm._id}`);
          if (parent) {
            parent.innerHTML = "";
            parent.appendChild(img);
          }
        };

        source.type = cm.mime;

        source.src = this.helper.formatMediaUrl(cm);

        img.setAttribute("controls", "controls");

        img.appendChild(source);
      } else {
        img.onload = () => {
          let parent = document.getElementById(`dashboardMedia${cm._id}`);

          if (parent) {
            parent.innerHTML = "";
            parent.appendChild(img);
          }
        };

        img.src = this.helper.formatMediaUrl(cm);
      }
    },
    getMoment(type, d) {
      if (!d) {
        return "-";
      } else {
        switch (type) {
          case "title":
            return Moment(d).format("DD MMMM YYYY, hh:mm A");
          case "display":
            return Moment(d).fromNow(false);
          default:
            return "";
        }
      }
    },
    onImageError(cl, event) {
      let image = event.target;

      image.onerror = null;

      image.src = require("@/assets/user.jpg");
    },
    viewCase(caseId) {
      this.$router.push({ name: "inbox", params: { id: caseId } });
    },
    getCaseIcon(type) {
      return Helper.socialMediaIcons(type);
    },
    getCases() {
      this.isProcessing = true;
      this.isLoaded = false;

      let url =
        "cases?timestamp_null=false&_sort=timestamp:DESC&_limit=-1&source=socialMedia&type_nin=waze&type_nin=instagram&complaintType_ne=sand_mining";

      if (this.dateFilter && this.dateFilter.start && this.dateFilter.end) {
        const start = Moment(this.dateFilter.start).startOf("day").toISOString();
        const end = Moment(this.dateFilter.end).endOf("day").toISOString();
        url += `&timestamp_gte=${start}&timestamp_lte=${end}`;
      }

      API.get(url)
        .then((retVal) => {
          this.cases = [];

          let rawCases = retVal.data;

          if (rawCases.length > 0) {
            rawCases.forEach((rc) => {
              let rcType = rc.type;

              let caseType = this.cases.find((c) => c.type == rcType);

              if (!caseType) {
                this.cases.push({ type: rcType, data: [rc], page: 1 });
              } else {
                caseType.data.push(rc);
              }
            });
          }

          this.isProcessing = false;
          this.isLoaded = true;
        })
        .catch((err) => {
          console.error(err);
          this.isProcessing = false;
          this.isLoaded = true;
        });
    },
    goToPage(cardIndex, n) {
      const card = this.cases[cardIndex];
      if (!card) return;
      const data = this.isLocal
        ? card.data.filter((d) => d.isLocal === true)
        : card.data;
      const totalPages = Math.max(1, Math.ceil(data.length / this.pageSize));
      const target = Math.max(1, Math.min(n, totalPages));
      if (target === (card.page || 1)) return;
      this.$set(card, "page", target);
    },
    computePaginationPages(current, total) {
      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      const pages = new Set([1, total, current, current - 1, current + 1]);
      if (current <= 3) {
        pages.add(2);
        pages.add(3);
      }
      if (current >= total - 2) {
        pages.add(total - 1);
        pages.add(total - 2);
      }
      const sorted = [...pages]
        .filter((p) => p >= 1 && p <= total)
        .sort((a, b) => a - b);
      const result = [];
      let prev = 0;
      for (const p of sorted) {
        if (p - prev > 1) result.push(null);
        result.push(p);
        prev = p;
      }
      return result;
    },
  },
  async mounted() {
    this.getCases();
  },
};
</script>
